import React, { useCallback, useEffect, useState } from 'react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography,
} from '@mui/material';
import NotificationsActiveRoundedIcon from '@mui/icons-material/NotificationsActiveRounded';
import { connect } from 'trim-redux';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import { requestPermission } from '../../../firebase';
import { markFcmAudioUnlocked } from '../../../config/fcmNotificationSound';
import { defaultVariables } from '../../../config';
import { isDesktopApp } from '../../../config/desktopApp';
import {
    dismissPushPromptForLater,
    fetchServerPushStatus,
    getWebNotificationPermission,
    shouldShowPushPrompt,
} from '../../../config/pushNotificationPrompt';

const PATHS_SKIP = ['/login', '/email-login', '/register', '/reset-password'];

function PushPermissionModal({ isAuth, user }) {
    const { t } = useTranslation();
    const { pathname } = useLocation();
    const { enqueueSnackbar } = useSnackbar();
    const [open, setOpen] = useState(false);
    const [permission, setPermission] = useState('default');
    const [serverStatus, setServerStatus] = useState(null);
    const [loading, setLoading] = useState(false);

    const evaluate = useCallback(async () => {
        if (!isAuth || !user?.id) {
            setOpen(false);
            return;
        }
        const skip = PATHS_SKIP.some((p) => pathname === p || pathname.startsWith(`${p}/`));
        if (skip) {
            return;
        }
        const perm = getWebNotificationPermission();
        setPermission(perm);
        let status = null;
        try {
            status = await fetchServerPushStatus(perm);
            setServerStatus(status);
        } catch {
            setServerStatus(null);
        }
        setOpen(shouldShowPushPrompt(isAuth, perm, status));
    }, [isAuth, user, pathname]);

    useEffect(() => {
        evaluate();
    }, [evaluate]);

    const saveToken = async (token) => {
        const loginToken = localStorage.getItem('ACT');
        await axios.post(
            `${defaultVariables.url}/firebase/save-token`,
            { token },
            {
                headers: {
                    Accept: 'application/json',
                    Authorization: `Bearer ${loginToken}`,
                },
            },
        );
    };

    const handleEnable = async () => {
        setLoading(true);
        try {
            if (isDesktopApp() && window.kolaydoyDesktop?.enableNotifications) {
                await window.kolaydoyDesktop.enableNotifications();
                setOpen(false);
                return;
            }

            const token = await requestPermission();
            const perm = getWebNotificationPermission();
            setPermission(perm);
            if (token) {
                markFcmAudioUnlocked();
                await saveToken(token);
                enqueueSnackbar(t('pushNotificationsEnabled'), { variant: 'success' });
                setOpen(false);
                return;
            }
            if (perm === 'denied') {
                enqueueSnackbar(t('pushNotificationsDeniedHelp'), { variant: 'warning' });
            } else {
                enqueueSnackbar(t('pushNotificationsEnableFailed'), { variant: 'error' });
            }
            await evaluate();
        } catch (e) {
            enqueueSnackbar(t('pushNotificationsEnableFailed'), { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleLater = () => {
        dismissPushPromptForLater();
        setOpen(false);
    };

    if (!open) {
        return null;
    }

    const isDenied = permission === 'denied';
    const missingToken = serverStatus && !serverStatus.push_ready && permission === 'granted';

    return (
        <Dialog open={open} onClose={handleLater} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <NotificationsActiveRoundedIcon color="primary" />
                {t('pushNotificationsPromptTitle')}
            </DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary" paragraph>
                    {t('pushNotificationsPromptBody')}
                </Typography>
                {isDenied && (
                    <Typography variant="body2" color="warning.main">
                        {t('pushNotificationsDeniedHelp')}
                    </Typography>
                )}
                {missingToken && (
                    <Typography variant="body2" color="info.main" sx={{ mt: 1 }}>
                        {t('pushNotificationsTokenMissing')}
                    </Typography>
                )}
                {serverStatus && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
                        {serverStatus.push_ready
                            ? t('pushNotificationsStatusReady')
                            : t('pushNotificationsStatusNotReady')}
                    </Typography>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={handleLater} color="inherit">
                    {t('pushNotificationsLater')}
                </Button>
                {!isDenied && (
                    <Button variant="contained" onClick={handleEnable} disabled={loading}>
                        {loading ? '...' : t('pushNotificationsEnable')}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
}

const mstp = (state) => ({ isAuth: state.isAuth, user: state.user });

export default connect(mstp)(PushPermissionModal);
